import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'semantic-ui-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Turnstile from 'react-turnstile';
import { StatusContext } from '../context/Status';
import {
  API,
  getCachedStatus,
  getRegistrationAvailability,
  showError,
  showInfo,
  showSuccess,
} from '../helpers';
import AuthLayout from './AuthLayout';

const RegisterForm = () => {
  const { t } = useTranslation();
  const [inputs, setInputs] = useState({
    username: '',
    password: '',
    password2: '',
    email: '',
    verification_code: '',
  });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationDisabled, setVerificationDisabled] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [statusState] = useContext(StatusContext);
  const navigate = useNavigate();
  const status = useMemo(
    () => statusState?.status || getCachedStatus(),
    [statusState?.status]
  );
  const registration = getRegistrationAvailability(status);
  const showEmailVerification = Boolean(status.email_verification);
  const turnstileEnabled = Boolean(status.turnstile_check);
  const turnstileSiteKey = status.turnstile_site_key || '';
  const { username, password, password2 } = inputs;

  useEffect(() => {
    const affCode = new URLSearchParams(window.location.search).get('aff');
    if (affCode) localStorage.setItem('aff', affCode);
  }, []);

  useEffect(() => {
    if (!verificationDisabled) return undefined;
    if (countdown <= 0) {
      setVerificationDisabled(false);
      setCountdown(30);
      return undefined;
    }
    const timer = setTimeout(
      () => setCountdown((current) => current - 1),
      1000
    );
    return () => clearTimeout(timer);
  }, [verificationDisabled, countdown]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!registration.enabled) {
      showInfo(t(`auth.register.disabled_${registration.reason}`));
      return;
    }
    if (registerLoading) return;
    if (password.length < 8) {
      showInfo(t('messages.error.password_length'));
      return;
    }
    if (password !== password2) {
      showInfo(t('messages.error.password_mismatch'));
      return;
    }
    if (!username || !password) return;
    if (turnstileEnabled && !turnstileToken) {
      showInfo(t('messages.error.turnstile_wait'));
      return;
    }

    setRegisterLoading(true);
    try {
      const affCode =
        new URLSearchParams(window.location.search).get('aff') ||
        localStorage.getItem('aff');
      const payload = { ...inputs, aff_code: affCode };
      const res = await API.post(
        `/api/user/register?turnstile=${turnstileToken}`,
        payload
      );
      if (!res?.data) return;
      const { success, message } = res.data;
      if (success) {
        navigate('/login');
        showSuccess(t('messages.success.register'));
      } else {
        showError(message);
      }
    } finally {
      setRegisterLoading(false);
    }
  };

  const sendVerificationCode = async () => {
    if (!registration.enabled) {
      showInfo(t(`auth.register.disabled_${registration.reason}`));
      return;
    }
    if (!inputs.email || verificationLoading || verificationDisabled) return;
    if (turnstileEnabled && !turnstileToken) {
      showInfo(t('messages.error.turnstile_wait'));
      return;
    }
    setVerificationDisabled(true);
    setVerificationLoading(true);
    try {
      const res = await API.get(
        `/api/verification?email=${encodeURIComponent(
          inputs.email
        )}&turnstile=${turnstileToken}`
      );
      if (!res?.data) {
        setVerificationDisabled(false);
        setCountdown(30);
        return;
      }
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('messages.success.verification_code'));
      } else {
        showError(message);
        setVerificationDisabled(false);
        setCountdown(30);
      }
    } finally {
      setVerificationLoading(false);
    }
  };

  return (
    <AuthLayout
      variant='register'
      title={t('auth.register.title')}
      subtitle={t('auth.register.subtitle')}
    >
      {registration.enabled ? (
        <>
          <Form size='large' onSubmit={handleSubmit}>
            <Form.Input
              fluid
              icon='user outline'
              iconPosition='left'
              placeholder={t('auth.register.username')}
              onChange={handleChange}
              value={inputs.username}
              name='username'
              autoComplete='username'
            />
            <Form.Input
              fluid
              icon='lock'
              iconPosition='left'
              placeholder={t('auth.register.password')}
              onChange={handleChange}
              value={inputs.password}
              name='password'
              type='password'
              autoComplete='new-password'
            />
            <Form.Input
              fluid
              icon='check circle outline'
              iconPosition='left'
              placeholder={t('auth.register.confirm_password')}
              onChange={handleChange}
              value={inputs.password2}
              name='password2'
              type='password'
              autoComplete='new-password'
            />

            {showEmailVerification && (
              <>
                <Form.Input
                  fluid
                  icon='mail outline'
                  iconPosition='left'
                  placeholder={t('auth.register.email')}
                  onChange={handleChange}
                  value={inputs.email}
                  name='email'
                  type='email'
                  className='auth-email-input'
                  action={
                    <Button
                      type='button'
                      onClick={sendVerificationCode}
                      loading={verificationLoading}
                      disabled={
                        verificationLoading ||
                        verificationDisabled ||
                        !inputs.email
                      }
                    >
                      {verificationDisabled
                        ? t('auth.register.get_code_retry', { countdown })
                        : t('auth.register.get_code')}
                    </Button>
                  }
                />
                <Form.Input
                  fluid
                  icon='key'
                  iconPosition='left'
                  placeholder={t('auth.register.verification_code')}
                  onChange={handleChange}
                  value={inputs.verification_code}
                  name='verification_code'
                />
              </>
            )}

            {turnstileEnabled && turnstileSiteKey && (
              <div className='auth-turnstile'>
                <Turnstile
                  sitekey={turnstileSiteKey}
                  size='flexible'
                  fixedSize
                  refreshExpired='auto'
                  onVerify={setTurnstileToken}
                  onExpire={() => setTurnstileToken('')}
                  onError={() => setTurnstileToken('')}
                />
              </div>
            )}

            <Button
              fluid
              type='submit'
              loading={registerLoading}
              disabled={registerLoading || !username || !password || !password2}
              className='auth-primary-button'
            >
              <span>{t('auth.register.button')}</span>
              <i className='arrow right icon' aria-hidden='true' />
            </Button>
          </Form>

          <div className='auth-register-prompt'>
            {t('auth.register.has_account')}
            <Link to='/login' className='auth-text-link'>
              {t('auth.register.login')}
            </Link>
          </div>
        </>
      ) : (
        <div className='auth-result auth-result-error' role='status'>
          <span className='auth-result-icon'>
            <i className='user times icon' aria-hidden='true' />
          </span>
          <strong>{t('auth.register.unavailable_title')}</strong>
          <p>{t(`auth.register.disabled_${registration.reason}`)}</p>
          <Link to='/login' className='auth-primary-link'>
            {t('auth.register.login')}
          </Link>
        </div>
      )}
    </AuthLayout>
  );
};

export default RegisterForm;
